/**
 * Documentos: repositorio, versoes, envio, download, exclusoes, tipos e configuracao.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams } from './comum';

import { api, requestPage } from '../client';

/**
 * Dono do documento. `seguradora` e a propria empresa (a lista "Documentos" do
 * OnPoint); `nomeacao` e `revisao_limite` guardam a carta de nomeacao e os
 * documentos do pedido de revisao de limite.
 */
export type DocumentOwnerType =
  | 'operacao'
  | 'apolice'
  | 'tomador'
  | 'corretor'
  | 'endosso'
  | 'sinistro'
  | 'segurado'
  | 'seguradora'
  | 'nomeacao'
  | 'revisao_limite';

/** Rotulo de cada dono, na ordem em que as telas o mostram. */
export const documentOwnerLabels: Record<DocumentOwnerType, string> = {
  operacao: 'Operacao',
  apolice: 'Apolice',
  tomador: 'Tomador',
  corretor: 'Corretor',
  endosso: 'Endosso',
  sinistro: 'Sinistro',
  segurado: 'Segurado',
  seguradora: 'Seguradora',
  nomeacao: 'Nomeacao',
  revisao_limite: 'Revisao de limite',
};

export type DocumentStatus = 'pendente' | 'recebido' | 'dispensado';
export type DocumentVersionState = 'reservada' | 'disponivel';

export interface DocumentVersion {
  id: string;
  document_id: string;
  version: number;
  filename: string;
  content_type: string;
  byte_size: number;
  checksum: string;
  state: DocumentVersionState;
  uploaded_label: string;
  uploaded_at: string;
  confirmed_at: string | null;
  /** Nome dado por quem enviou. */
  display_name?: string;
  /** Comentario de quem enviou. */
  comment?: string;
}

export interface DocumentRecord {
  id: string;
  number: number;
  owner_type: DocumentOwnerType;
  owner_id: string;
  group: string;
  title: string;
  required: boolean;
  status: DocumentStatus;
  current_version: number;
  template_id: string | null;
  template_version: number | null;
  /** Tipo do catalogo da empresa. */
  document_type_id?: string | null;
  /** Descricao da situacao. */
  status_note?: string;
  /** Validade informada, AAAA-MM-DD. */
  valid_until?: string | null;
  /** A validade ja passou. So leitura: nenhuma regra usa. */
  expired?: boolean;
  /** Item do catalogo de documentos pedidos ao corretor de que veio (P-COR-1). */
  request_item_id?: string | null;
  /** Cartao da esteira em que o documento foi pedido (P-EST-1). */
  pipe_card_id?: string | null;
  created_at: string;
  updated_at: string;
  /** Quem inseriu o documento (P-COT-3). Nulo quando a carga nao soube dizer. */
  origin?: DocumentOrigin | null;
  /** Pedido ao corretor (P-COT-3): quando, quem pediu e o comentario. */
  requested_at?: string | null;
  requested_label?: string;
  request_note?: string;
}

/** Origem do documento: o lado de quem o inseriu (P-COT-3). */
export type DocumentOrigin = 'corretor' | 'seguradora' | 'sistema';

export interface DocumentDetail extends DocumentRecord {
  versions: DocumentVersion[];
}

export interface DocumentInput {
  owner_type: DocumentOwnerType;
  /** Pode faltar no documento da seguradora: o dono e a propria empresa. */
  owner_id?: string;
  group?: string;
  title: string;
  required?: boolean;
  document_type_id?: string;
  status_note?: string;
  valid_until?: string;
  /** Cartao da esteira em que o documento e pedido (P-EST-1). */
  pipe_card_id?: string;
}

/**
 * Edicao dos metadados. Tipo, descricao e validade ausentes ficam como estao; texto
 * vazio no tipo ou na validade os retira.
 */
export interface DocumentUpdateInput {
  group?: string;
  title: string;
  required?: boolean;
  waived?: boolean;
  document_type_id?: string;
  status_note?: string;
  valid_until?: string;
}

export interface DocumentUploadInput {
  filename: string;
  content_type: string;
  byte_size: number;
  checksum: string;
  display_name?: string;
  comment?: string;
}

export interface DocumentUploadTicket {
  version: DocumentVersion;
  upload_url: string;
  expires_at: string;
  /**
   * O envio deixa a empresa acima da cota de disco que a administracao da plataforma
   * permite ultrapassar (P-PLAT). Ausentes nas respostas antigas.
   */
  over_quota?: boolean;
  quota_warning?: string | null;
}

export interface DocumentDownload {
  url: string;
  filename: string;
  expires_at: string;
}

/**
 * Estado do armazenamento de arquivos.
 *
 * A tela precisa saber se pode oferecer o envio antes de a pessoa escolher um
 * arquivo. Quando indisponivel, `missing_requirement` diz o que falta, e a tela
 * mostra isso em vez de um botao que sempre falha.
 */
export interface DocumentStorage {
  available: boolean;
  provider: string;
  max_bytes: number;
  accepted_types: Record<string, string>;
  missing_requirement: string;
  /**
   * Uso da empresa e cota. Ausentes nas respostas antigas. A cota e definida pela
   * administracao da plataforma (P-PLAT).
   */
  used_bytes?: number;
  quota_bytes?: number | null;
  quota_gb?: number | null;
  quota_soft?: boolean;
  over_quota?: boolean;
}

export interface DocumentDeletion {
  id: string;
  document_id: string;
  number: number;
  owner_type: string;
  owner_id: string;
  group_name: string;
  title: string;
  inserted_at: string;
  deleted_label: string;
  deleted_at: string;
  /** A exclusao guardou o arquivo. */
  file_retained?: boolean;
  /** Ha arquivo guardado para baixar. */
  file_available?: boolean;
  /** O documento pode voltar. */
  restorable?: boolean;
  /** Origem do documento excluido (P-COT-3). */
  origin?: DocumentOrigin | null;
}

/** Tipo do catalogo de documentos da empresa. */
export interface DocumentType {
  id: string;
  name: string;
  description: string;
  /** Donos em que o tipo e oferecido. Vazio vale para todos. */
  owner_types: DocumentOwnerType[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocumentTypeInput {
  name: string;
  description?: string;
  owner_types?: DocumentOwnerType[];
  active?: boolean;
}

/**
 * Configuracao do repositorio: guarda do arquivo excluido e, so para leitura, a cota
 * de disco definida pela administracao da plataforma (P-PLAT).
 */
export interface DocumentSettings {
  retain_deleted_files: boolean;
  storage_quota_bytes: number | null;
  storage_quota_gb?: number | null;
  storage_quota_soft: boolean;
  updated_label: string;
  updated_at: string | null;
}

/** So a guarda: a cota nao se grava na configuracao do repositorio. */
export interface DocumentSettingsInput {
  retain_deleted_files: boolean;
}

/** Codigo do envio que passaria da cota rigida da empresa (422). */
export const DOCUMENT_QUOTA_EXCEEDED = 'DOCUMENTOS_COTA_EXCEDIDA';

export async function listDocuments(
  params: ListParams,
  signal?: AbortSignal,
): Promise<{
  items: DocumentRecord[];
  total: number;
  limit: number;
  offset: number;
  byStatus: Record<string, number>;
}> {
  const page = await requestPage<DocumentRecord>(
    `/api/v1/documents${query(params)}`,
    signal,
  );
  const byStatus = (page.meta.by_status as Record<string, number> | undefined) ?? {};
  return { ...page, byStatus };
}

export const documents = {
  list: listDocuments,
  get: (id: string, signal?: AbortSignal) =>
    api.get<DocumentDetail>(`/api/v1/documents/${id}`, signal),
  require: (input: DocumentInput) =>
    api.post<DocumentRecord>('/api/v1/documents', input),
  update: (id: string, input: DocumentUpdateInput) =>
    api.patch<DocumentRecord>(`/api/v1/documents/${id}`, input),
  remove: (id: string) => api.delete(`/api/v1/documents/${id}`),
  storage: (signal?: AbortSignal) =>
    api.get<DocumentStorage>('/api/v1/documents/storage', signal),
  startUpload: (id: string, input: DocumentUploadInput) =>
    api.post<DocumentUploadTicket>(`/api/v1/documents/${id}/uploads`, input),
  confirmUpload: (id: string, versionId: string) =>
    api.post<DocumentRecord>(
      `/api/v1/documents/${id}/uploads/${versionId}/confirmation`,
      {},
    ),
  download: (id: string, version?: number) =>
    api.get<DocumentDownload>(
      `/api/v1/documents/${id}/download${version ? `?version=${version}` : ''}`,
    ),

  /** Lista "Documentos excluidos" de um dono; a seguradora dispensa o vinculo. */
  deletions: (ownerType: DocumentOwnerType, ownerId?: string, signal?: AbortSignal) =>
    api.get<DocumentDeletion[]>(
      `/api/v1/documents/deletions${query({ owner_type: ownerType, owner_id: ownerId })}`,
      signal,
    ),
  restoreDeletion: (deletionId: string) =>
    api.post<DocumentRecord>(
      `/api/v1/documents/deletions/${deletionId}/restoration`,
      {},
    ),
  downloadDeletion: (deletionId: string) =>
    api.get<DocumentDownload>(`/api/v1/documents/deletions/${deletionId}/download`),

  types: (params: ListParams, signal?: AbortSignal) =>
    requestPage<DocumentType>(`/api/v1/document-types${query(params)}`, signal),
  createType: (input: DocumentTypeInput) =>
    api.post<DocumentType>('/api/v1/document-types', input),
  updateType: (id: string, input: DocumentTypeInput) =>
    api.patch<DocumentType>(`/api/v1/document-types/${id}`, input),

  settings: (signal?: AbortSignal) =>
    api.get<DocumentSettings>('/api/v1/documents/settings', signal),
  updateSettings: (input: DocumentSettingsInput) =>
    api.put<DocumentSettings>('/api/v1/documents/settings', input),
};
